'use client'

import { useCallback, useRef, useState } from 'react'

// Shared state machine for "AI generate" widgets across the backoffice —
// extracted from the CTT campaign generator so new AI widgets (batch email
// type generation, etc.) get the same fetch/abort/timeout/error handling for
// free instead of re-implementing it per widget. The actual prompt/model
// logic lives server-side (usually proxied to the external Python AI
// service); this hook only owns the client-side request lifecycle.
export function useAIGenerate<TResult = any>(endpoint: string, { timeoutMs = 8000 }: { timeoutMs?: number } = {}) {
  const [generating, setGenerating] = useState(false)
  const [generatedOnce, setGeneratedOnce] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const controllerRef = useRef<AbortController | null>(null)
  const generatingRef = useRef(false)

  const generate = useCallback(async (payload: unknown): Promise<TResult | undefined> => {
    if (generatingRef.current) return undefined
    generatingRef.current = true
    setGenerating(true)
    setError(null)

    const controller = new AbortController()
    controllerRef.current = controller
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify(payload),
      })
      const result = await res.json()
      if (result.success) {
        setGeneratedOnce(true)
        return result.data as TResult
      }
      setError(result.error ?? 'Generation failed.')
      return undefined
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error.')
      return undefined
    } finally {
      clearTimeout(timer)
      controllerRef.current = null
      generatingRef.current = false
      setGenerating(false)
    }
  }, [endpoint, timeoutMs])

  const cancel = useCallback(() => controllerRef.current?.abort(), [])

  return { generate, cancel, generating, generatedOnce, error, setError }
}
